/**
 * Generic retry utility with exponential backoff and jitter.
 * Use to wrap unreliable external service calls (Cloudinary, Google Sheets, Supabase, Fal.ai).
 */

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay in milliseconds before first retry (default: 1000) */
  baseDelayMs?: number;
  /** Predicate to decide whether a given error is retryable (default: always retry) */
  shouldRetry?: (error: unknown) => boolean;
}

/**
 * Compute delay with exponential backoff and jitter.
 * Formula: baseDelayMs * 2^attempt * (0.5..1.0 random jitter)
 */
export function computeDelay(baseDelayMs: number, attempt: number): number {
  const exponential = baseDelayMs * Math.pow(2, attempt);
  const jitter = 0.5 + Math.random() * 0.5;
  return Math.round(exponential * jitter);
}

/**
 * Execute an async function with automatic retries using exponential backoff.
 *
 * @param fn - The async function to execute
 * @param options - Retry configuration
 * @returns The resolved value of fn
 * @throws The last error encountered after all retries are exhausted,
 *         or immediately if shouldRetry returns false
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: RetryOptions,
): Promise<T> {
  const maxRetries = options?.maxRetries ?? 3;
  const baseDelayMs = options?.baseDelayMs ?? 1000;
  const shouldRetry = options?.shouldRetry ?? (() => true);

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      const isLastAttempt = attempt === maxRetries;
      if (isLastAttempt || !shouldRetry(error)) {
        throw error;
      }

      const delay = computeDelay(baseDelayMs, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Unreachable, but TypeScript needs it
  throw lastError;
}

/**
 * Predicate: returns true for errors that look like retryable HTTP errors.
 * Skips retry on 4xx client errors (except 429 rate-limit).
 */
export function isRetryableHttpError(error: unknown): boolean {
  if (!(error instanceof Error)) return true;

  const message = error.message;
  const statusMatch = message.match(/(?:status\s*)?(\d{3})/i);
  if (!statusMatch?.[1]) return true; // No status code found — assume retryable

  const status = parseInt(statusMatch[1], 10);

  // 429 (rate limit) is retryable; other 4xx are not
  if (status === 429) return true;
  if (status >= 400 && status < 500) return false;

  return true;
}

/**
 * Predicate for Fal.ai service calls.
 * Retries on 429, 500, 502, 503. Skips 401 (auth) and 413 (too large).
 */
export function isRetryableFalError(error: unknown): boolean {
  if (!(error instanceof Error)) return true;

  const message = error.message;
  const statusMatch = message.match(/(?:status\s*)?(\d{3})/i);
  if (!statusMatch?.[1]) return true;

  const status = parseInt(statusMatch[1], 10);
  const retryableStatuses = new Set([429, 500, 502, 503]);

  return retryableStatuses.has(status);
}
